/**
 * Asignación de atributos: 20 aleatorios (1 reroll) + 30 manuales = 50.
 * Usado en create wizard y en reasignación forzada de ficha.
 */

export const STAT_KEYS = [
  { key: 'fuerza', label: 'Fuerza' },
  { key: 'destreza', label: 'Destreza' },
  { key: 'agilidad', label: 'Agilidad' },
  { key: 'resistencia', label: 'Resistencia' },
  { key: 'voluntad', label: 'Voluntad' },
  { key: 'controlChakra', label: 'Control de Chakra' },
  { key: 'concentracion', label: 'Concentración' },
  { key: 'inteligencia', label: 'Inteligencia' },
]

export const STAT_KEY_LIST = STAT_KEYS.map((s) => s.key)

export const RANDOM_POINTS = 20
export const MANUAL_POINTS = 30
export const TOTAL_POINTS = RANDOM_POINTS + MANUAL_POINTS
export const INITIAL_REROLLS = 1
/** Cada atributo de la tirada aleatoria queda entre estos extremos (suma = RANDOM_POINTS). */
export const RANDOM_STAT_MIN = 1
export const RANDOM_STAT_MAX = 4

export const ZERO_STATS = Object.freeze({
  fuerza: 0,
  destreza: 0,
  agilidad: 0,
  resistencia: 0,
  voluntad: 0,
  controlChakra: 0,
  concentracion: 0,
  inteligencia: 0,
})

export function emptyStats() {
  return { ...ZERO_STATS }
}

/**
 * Reparto equilibrado: cada attr empieza en RANDOM_STAT_MIN y se reparte el resto
 * al azar sin superar RANDOM_STAT_MAX (8 attrs × [1..4] = suma 20).
 */
export function rollRandomStats(rng = Math.random) {
  const keys = STAT_KEY_LIST
  const out = emptyStats()
  for (const key of keys) {
    out[key] = RANDOM_STAT_MIN
  }
  let remaining = RANDOM_POINTS - keys.length * RANDOM_STAT_MIN
  while (remaining > 0) {
    const eligible = keys.filter((k) => out[k] < RANDOM_STAT_MAX)
    if (!eligible.length) break
    const pick = eligible[Math.floor(rng() * eligible.length)]
    out[pick] += 1
    remaining -= 1
  }
  return out
}

export function sumStats(stats) {
  return STAT_KEY_LIST.reduce((acc, key) => {
    const n = parseInt(stats?.[key] ?? 0, 10)
    return acc + (Number.isNaN(n) ? 0 : n)
  }, 0)
}

export function mergeStats(randomStats, manualStats) {
  const out = emptyStats()
  for (const key of STAT_KEY_LIST) {
    const r = parseInt(randomStats?.[key] ?? 0, 10) || 0
    const m = parseInt(manualStats?.[key] ?? 0, 10) || 0
    out[key] = Math.max(0, r) + Math.max(0, m)
  }
  return out
}

/**
 * @returns {string[]} errores (vacío = ok)
 */
export function validateAllocation(randomStats, manualStats) {
  const errs = []
  const randomSum = sumStats(randomStats)
  const manualSum = sumStats(manualStats)
  const total = mergeStats(randomStats, manualStats)
  const totalSum = sumStats(total)

  if (randomSum !== RANDOM_POINTS) {
    errs.push(`La base aleatoria debe sumar ${RANDOM_POINTS} (ahora ${randomSum}).`)
  }
  if (manualSum !== MANUAL_POINTS) {
    errs.push(
      `Debes asignar exactamente ${MANUAL_POINTS} puntos manuales. Te quedan ${MANUAL_POINTS - manualSum}.`
    )
  }
  if (totalSum !== TOTAL_POINTS) {
    errs.push(`El total de atributos debe ser ${TOTAL_POINTS} (ahora ${totalSum}).`)
  }
  for (const { key, label } of STAT_KEYS) {
    const n = parseInt(total[key], 10)
    if (Number.isNaN(n) || n < 0) {
      errs.push(`${label} debe ser un entero ≥ 0`)
    }
    const r = parseInt(randomStats?.[key] ?? 0, 10)
    if (Number.isNaN(r) || r < RANDOM_STAT_MIN || r > RANDOM_STAT_MAX) {
      errs.push(`${label} (base aleatoria) debe estar entre ${RANDOM_STAT_MIN} y ${RANDOM_STAT_MAX}`)
    }
    const m = parseInt(manualStats?.[key] ?? 0, 10)
    if (Number.isNaN(m) || m < 0) {
      errs.push(`${label} (manual) no puede ser negativo`)
    }
  }
  return errs
}

/** Ajusta solo la capa manual; no baja de 0. Respeta el techo de puntos libres. */
export function setManualStat(manualStats, key, nextRaw, pointsBudget = MANUAL_POINTS) {
  const cur = { ...emptyStats(), ...manualStats }
  const prev = parseInt(cur[key] ?? 0, 10) || 0
  let next = parseInt(nextRaw, 10)
  if (Number.isNaN(next)) next = 0
  next = Math.max(0, next)

  const usedWithout = sumStats(cur) - prev
  const maxForKey = Math.max(0, pointsBudget - usedWithout)
  cur[key] = Math.min(next, maxForKey)
  return cur
}
